/**
 * «Кассы CRM → Кассы»: выплаты касс CRM — наличные (деньги мимо банка) и безнал
 * (проведённый документ; сами деньги уйдут операцией банка и придут выпиской).
 * У строки — заявка, по которой выплачено, и комментарии CRM к обеим; свой
 * комментарий (куда разнести, что выяснили) пишется здесь же, в CRM он не уходит.
 * Вкладка «Оплаты наличными» — наличная выручка касс.
 *
 * Фильтры живут в адресе: из P&L сюда приходят по ссылке «статья × ИП».
 * Спека: `docs/specs/2026-09-29-kassa-expenses-design.md`.
 */
import { MessageSquare, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { Combobox } from "@/components/Combobox";
import { DateRangePicker, withPeriod } from "@/components/DateRangePicker";
import { EntrepreneurPicker, entrepreneurIds, withEntrepreneurs } from "@/components/EntrepreneurPicker";
import { NoAccess } from "@/components/NoAccess";
import { KASSA, KASSA_EDIT, PNL_EDIT, useCan } from "@/lib/access";
import { mskTime } from "@/lib/dates";
import {
  addKassaNote,
  KASSA_KIND_LABEL,
  type Kassa,
  type KassaComment,
  type KassaExpense,
  type KassaExpensePage,
  type KassaKind,
  type KassaNote,
  type KassaPayment,
  type KassaPaymentPage,
  type KassaSync,
  loadKassaExpenses,
  loadKassaPayments,
  loadKassas,
  loadLastSync,
  runSync,
} from "@/lib/kassa";
import { type Article, loadArticles, money } from "@/lib/pnl";
import { type DirectoryRow, ENTREPRENEURS, loadDirectory } from "@/lib/statements";

const INPUT =
  "h-8 rounded-[var(--radius-ui)] border border-border bg-bg px-2 text-[12.5px] outline-none focus:border-border-strong";
const TH = "border-b border-border px-2.5 py-2 text-left";
const TD = "border-b border-border px-2.5 py-1.5 align-top";
const LIMIT = 100;

export function KassaPage() {
  return useCan(KASSA) ? <KassaScreen /> : <NoAccess what="расходам касс" />;
}

function numberParam(value: string | null): number | undefined {
  return value ? Number(value) : undefined;
}

function when(iso: string): string {
  const [day, time] = iso.split("T");
  return `${day?.split("-").reverse().join(".")} ${time?.slice(0, 5) ?? ""}`.trim();
}

function KassaScreen() {
  const canEdit = useCan(KASSA_EDIT);
  // Комментарий — то же право, что у комментария к операции банка.
  const canNote = useCan(PNL_EDIT);
  const [params, setParams] = useSearchParams();
  const [kassas, setKassas] = useState<Kassa[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [entrepreneurs, setEntrepreneurs] = useState<DirectoryRow[]>([]);
  const [page, setPage] = useState<KassaExpensePage | null>(null);
  const [paid, setPaid] = useState<KassaPaymentPage | null>(null);
  const [sync, setSync] = useState<KassaSync | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [failure, setFailure] = useState("");
  const [notice, setNotice] = useState("");
  const [searchDraft, setSearchDraft] = useState(params.get("search") ?? "");
  const [reloadKey, setReloadKey] = useState(0);

  // Вкладка: выплаты из касс (расход) или оплаты наличными (выручка).
  const payments = params.get("flow") === "payments";
  const filter = useMemo(
    () => ({
      kassa_id: numberParam(params.get("kassa_id")),
      kind: (params.get("kind") as KassaKind | null) ?? undefined,
      entrepreneur_id: entrepreneurIds(params),
      crm_article_id: numberParam(params.get("crm_article_id")),
      article_id: numberParam(params.get("article_id")),
      location_id: numberParam(params.get("location_id")),
      user_crm_id: numberParam(params.get("user_crm_id")),
      role: params.get("role") ?? undefined,
      search: params.get("search") ?? undefined,
      date_from: params.get("date_from") ?? undefined,
      date_to: params.get("date_to") ?? undefined,
      deleted: params.get("deleted") === "true",
      limit: LIMIT,
      offset: Number(params.get("offset") ?? 0),
    }),
    [params],
  );

  const setFilter = (key: string, value: string | number | undefined) => {
    const next = new URLSearchParams(params);
    if (value === undefined || value === "") next.delete(key);
    else next.set(key, String(value));
    if (key !== "offset") next.delete("offset");
    setParams(next);
  };

  const setPeriod = (from: string, to: string) => {
    const next = withPeriod(params, from, to);
    next.delete("offset");
    setParams(next);
  };

  useEffect(() => {
    loadKassas()
      .then(setKassas)
      .catch((error: Error) => setFailure(error.message));
    loadArticles()
      .then(setArticles)
      .catch(() => setArticles([])); // без права P&L статьи просто не фильтруются
    loadDirectory(ENTREPRENEURS, true)
      .then(setEntrepreneurs)
      .catch(() => setEntrepreneurs([])); // справочник ИП — право «Выписки»
  }, [reloadKey]);

  useEffect(() => {
    loadLastSync()
      .then(setSync)
      .catch((error: Error) => setFailure(error.message));
  }, [reloadKey]);

  useEffect(() => {
    if (payments) {
      setPaid(null);
      loadKassaPayments({
        kassa_id: filter.kassa_id,
        entrepreneur_id: filter.entrepreneur_id,
        date_from: filter.date_from,
        date_to: filter.date_to,
        deleted: filter.deleted,
        limit: LIMIT,
        offset: filter.offset,
      })
        .then(setPaid)
        .catch((error: Error) => setFailure(error.message));
      return;
    }
    setPage(null);
    loadKassaExpenses(filter)
      .then(setPage)
      .catch((error: Error) => setFailure(error.message));
  }, [payments, filter, reloadKey]);

  const switchFlow = (flow: "payments" | undefined) => {
    const next = withEntrepreneurs(new URLSearchParams(), entrepreneurIds(params));
    for (const key of ["date_from", "date_to"]) {
      const value = params.get(key);
      if (value) next.set(key, value);
    }
    if (flow) next.set("flow", flow);
    setParams(next);
  };

  const doSync = () => {
    setSyncing(true);
    setFailure("");
    setNotice("");
    runSync()
      .then((r) =>
        setNotice(
          `Загружено из CRM: выплат ${r.expenses ?? 0} (новых ${r.expenses_new ?? 0}, пропало ${r.expenses_gone ?? 0}), ` +
            `заявок ${r.invoices ?? 0}, комментариев ${r.comments ?? 0}; ` +
            `оплат наличными ${r.payments ?? 0} (новых ${r.payments_new ?? 0}, пропало ${r.payments_gone ?? 0})`,
        ),
      )
      .catch((error: Error) => setFailure(error.message))
      .finally(() => {
        setSyncing(false);
        setReloadKey((k) => k + 1);
      });
  };

  const articleName = articles.find((a) => a.id === filter.article_id)?.name;

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4 [&>*]:flex-none">
      <div className="flex items-center gap-1 text-[12.5px]">
        {([
          [undefined, "Выплаты из касс"],
          ["payments", "Оплаты наличными"],
        ] as const).map(([flow, label]) => {
          const active = (flow === "payments") === payments;
          return (
            <button
              key={label}
              type="button"
              onClick={() => switchFlow(flow)}
              className={`h-8 rounded-[var(--radius-ui)] px-3 ${
                active ? "bg-bg-hover font-medium text-fg" : "text-fg-muted hover:bg-bg-hover hover:text-fg"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        {payments ? null : (
          <Combobox
            value={filter.kind ?? ""}
            onChange={(v) => setFilter("kind", v)}
            options={[
              { value: "", label: "Все виды касс" },
              { value: "cash", label: "Наличные" },
              { value: "document", label: "Безнал (документ)" },
              { value: "internal", label: "Внутренние" },
            ]}
          />
        )}
        <Combobox
          className="max-w-[240px]"
          value={String(filter.kassa_id ?? "")}
          onChange={(v) => setFilter("kassa_id", v)}
          options={[
            { value: "", label: "Все кассы" },
            ...kassas
              .filter((k) => (payments ? k.kind === "cash" : !filter.kind || k.kind === filter.kind))
              .map((k) => ({ value: String(k.id), label: k.name })),
          ]}
        />
        {entrepreneurs.length ? (
          <EntrepreneurPicker
            entrepreneurs={entrepreneurs}
            value={filter.entrepreneur_id}
            onChange={(ids) => {
              const next = withEntrepreneurs(params, ids);
              next.delete("offset");
              setParams(next);
            }}
          />
        ) : null}
        <DateRangePicker label="период" from={filter.date_from ?? ""} to={filter.date_to ?? ""} onChange={setPeriod} />
        {payments ? null : (
          <form
            className="flex items-center gap-1"
            onSubmit={(e) => {
              e.preventDefault();
              setFilter("search", searchDraft.trim());
            }}
          >
            <input
              className={`${INPUT} w-[220px]`}
              placeholder="комментарий, тип расхода"
              value={searchDraft}
              onChange={(e) => setSearchDraft(e.target.value)}
            />
          </form>
        )}
        <label className="flex items-center gap-1.5 text-fg-muted">
          <input
            type="checkbox"
            checked={filter.deleted}
            onChange={(e) => setFilter("deleted", e.target.checked ? "true" : undefined)}
          />
          пропавшие в CRM
        </label>
        {canEdit ? (
          <button
            type="button"
            onClick={doSync}
            disabled={syncing}
            className="ml-auto flex h-8 items-center gap-1.5 rounded-[var(--radius-ui)] bg-fg px-3 font-medium text-bg disabled:opacity-50"
          >
            <RefreshCw size={13} className={syncing ? "animate-spin" : ""} />
            {syncing ? "Загружаем…" : "Обновить из CRM"}
          </button>
        ) : null}
      </div>

      <SyncLine sync={sync} />

      {filter.article_id || filter.crm_article_id ? (
        <div className="flex items-center gap-2 text-[12px] text-fg-muted">
          <span>
            Статья: {articleName ?? (filter.crm_article_id ? `CRM №${filter.crm_article_id}` : `№${filter.article_id}`)}
          </span>
          <button
            type="button"
            className="text-accent-text hover:underline"
            onClick={() => {
              const next = new URLSearchParams(params);
              next.delete("article_id");
              next.delete("crm_article_id");
              next.delete("offset");
              setParams(next);
            }}
          >
            снять
          </button>
        </div>
      ) : null}

      {filter.location_id || filter.user_crm_id || filter.role ? (
        <div className="flex items-center gap-2 text-[12px] text-fg-muted">
          <span>
            Из «Зарплаты»:{" "}
            {params.get("label") ??
              (filter.user_crm_id
                ? `сотрудник CRM №${filter.user_crm_id}`
                : filter.role
                  ? `роль «${filter.role}»`
                  : `салон №${filter.location_id}`)}
          </span>
          <button
            type="button"
            className="text-accent-text hover:underline"
            onClick={() => {
              const next = new URLSearchParams(params);
              for (const key of ["location_id", "user_crm_id", "role", "label", "offset"]) next.delete(key);
              setParams(next);
            }}
          >
            снять
          </button>
        </div>
      ) : null}

      {failure ? (
        <div role="alert" className="rounded-[var(--radius-ui)] border border-err/30 bg-err-bg px-3 py-2 text-[12.5px] text-err">
          {failure}
        </div>
      ) : null}
      {notice ? <div className="text-[12.5px] text-ok">{notice}</div> : null}

      {payments ? (
        <PaymentsTable page={paid} offset={filter.offset} onOffset={(o) => setFilter("offset", o || undefined)} />
      ) : page === null ? (
        <div className="text-[12.5px] text-fg-muted">…</div>
      ) : page.total === 0 && !sync ? (
        <div className="text-[12.5px] text-fg-muted">
          Расходов касс ещё нет — {canEdit ? "нажмите «Обновить из CRM»." : "загрузку запускает тот, у кого право «Обновлять кассы»."}
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3 text-[12px] text-fg-muted">
            <span>
              Выплат: {page.total} · на {money(page.sum)} ₽
            </span>
            {filter.kind === "document" ? (
              <span className="text-fg-faint">безнал — документ; деньги в P&L придут выпиской банка</span>
            ) : null}
            <Pager total={page.total} offset={filter.offset} onOffset={(o) => setFilter("offset", o || undefined)} />
          </div>
          <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
            <table className="w-full border-collapse text-[12.5px]">
              <thead>
                <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
                  <th className={TH}>дата</th>
                  <th className={`${TH} text-right`}>сумма</th>
                  <th className={TH}>касса</th>
                  <th className={TH}>тип · получатель</th>
                  <th className={TH}>комментарий</th>
                  <th className={TH}>заявка</th>
                  <th className={TH}>статья</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => (
                  <ExpenseRow
                    key={row.id}
                    row={row}
                    canNote={canNote}
                    onNotes={(notes) =>
                      setPage((prev) =>
                        prev && { ...prev, rows: prev.rows.map((r) => (r.id === row.id ? { ...r, notes } : r)) },
                      )
                    }
                    onFailure={setFailure}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}

function PaymentsTable(props: { page: KassaPaymentPage | null; offset: number; onOffset: (offset: number) => void }) {
  const { page } = props;
  if (page === null) return <div className="text-[12.5px] text-fg-muted">…</div>;
  return (
    <>
      <div className="flex items-center gap-3 text-[12px] text-fg-muted">
        <span>
          Оплат: {page.total} · на {money(page.sum)} ₽
        </span>
        <span className="text-fg-faint">наличная выручка; её инкассация в банк — перевод между своими</span>
        <Pager total={page.total} offset={props.offset} onOffset={props.onOffset} />
      </div>
      <div className="overflow-x-auto rounded-[var(--radius-card)] border border-border">
        <table className="w-full border-collapse text-[12.5px]">
          <thead>
            <tr className="bg-bg-subtle text-[11px] font-medium text-fg-muted">
              <th className={TH}>дата</th>
              <th className={`${TH} text-right`}>сумма</th>
              <th className={TH}>касса</th>
              <th className={TH}>категория</th>
              <th className={TH}>сделка CRM</th>
            </tr>
          </thead>
          <tbody>
            {page.rows.map((row) => (
              <PaymentRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PaymentRow({ row }: { row: KassaPayment }) {
  return (
    <tr data-annot={`платёж CRM ${row.crm_id}`} className={`hover:bg-bg-hover ${row.date_deleted ? "opacity-60" : ""}`}>
      <td className={`${TD} whitespace-nowrap tabular-nums text-fg-muted`}>
        {when(row.date)}
        {row.date_deleted ? <div className="text-[10.5px] text-err">отменена в CRM</div> : null}
      </td>
      <td className={`${TD} whitespace-nowrap text-right tabular-nums text-ok`}>+{money(row.amount)}</td>
      <td className={`${TD} whitespace-nowrap text-[11.5px]`}>
        <div>{row.kassa_name}</div>
        <div className="text-fg-faint">
          {row.entrepreneur_name ?? ""}
          {row.location_name ? ` · ${row.location_name}` : ""}
        </div>
      </td>
      <td className={`${TD} text-[11.5px]`}>{row.category || "—"}</td>
      <td className={`${TD} tabular-nums text-[11.5px] text-fg-muted`}>{row.deal_crm_id ?? "—"}</td>
    </tr>
  );
}

function SyncLine({ sync }: { sync: KassaSync | null }) {
  if (!sync) return null;
  if (sync.error) {
    return (
      <div className="text-[12px] text-err">
        Последняя загрузка {when(sync.date_start)} не прошла: {sync.error}
      </div>
    );
  }
  if (!sync.date_finish) return <div className="text-[12px] text-fg-muted">Загрузка идёт с {when(sync.date_start)}…</div>;
  return (
    <div className="text-[12px] text-fg-faint">
      Из CRM загружено {when(sync.date_finish)} · выплаты с {sync.result.since?.split("-").reverse().join(".")} ·{" "}
      <Link to="/kassa/articles" className="text-accent-text hover:underline">
        статьи CRM
      </Link>
    </div>
  );
}

export function Pager(props: { total: number; offset: number; onOffset: (offset: number) => void }) {
  if (props.total <= LIMIT) return null;
  const last = props.offset + LIMIT >= props.total;
  return (
    <span className="ml-auto flex items-center gap-2">
      <button
        type="button"
        disabled={props.offset === 0}
        onClick={() => props.onOffset(Math.max(0, props.offset - LIMIT))}
        className="rounded border border-border px-2 py-0.5 disabled:opacity-40"
      >
        ←
      </button>
      {props.offset + 1}–{Math.min(props.offset + LIMIT, props.total)} из {props.total}
      <button
        type="button"
        disabled={last}
        onClick={() => props.onOffset(props.offset + LIMIT)}
        className="rounded border border-border px-2 py-0.5 disabled:opacity-40"
      >
        →
      </button>
    </span>
  );
}

function Comments({ items }: { items: KassaComment[] }) {
  if (!items.length) return null;
  return (
    <ul className="mt-1 grid gap-0.5">
      {items.map((c) => (
        <li key={`${c.date}-${c.text}`} className="text-[11.5px] text-fg-muted">
          <span className="text-fg-faint">{when(c.date).split(" ")[0]} · </span>
          {c.text}
        </li>
      ))}
    </ul>
  );
}

/** Наши комментарии: кто написал и когда — по ним разносят выплату. */
function Notes({ items }: { items: KassaNote[] }) {
  return items.map((n) => (
    <div key={n.id} className="mt-1 rounded-[var(--radius-ui)] bg-bg-subtle px-2 py-1 text-[12px] text-fg">
      <span className="whitespace-pre-wrap">{n.text}</span>
      <span className="ml-1.5 text-[10.5px] text-fg-faint">
        — {n.user_name}, {mskTime(n.date)}
      </span>
    </div>
  ));
}

function NoteForm(props: { id: number; onSaved: (notes: KassaNote[]) => void; onFailure: (m: string) => void }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const save = () => {
    setBusy(true);
    addKassaNote(props.id, text)
      .then(props.onSaved)
      .catch((error: Error) => props.onFailure(error.message))
      .finally(() => setBusy(false));
  };
  return (
    <div className="grid max-w-[820px] gap-2 text-[12.5px]">
      <textarea
        autoFocus
        rows={3}
        maxLength={4000}
        className="rounded-[var(--radius-ui)] border border-border bg-bg px-2 py-1.5 outline-none focus:border-border-strong"
        placeholder="Куда разнести эту выплату, что выяснили"
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      <div>
        <button
          type="button"
          disabled={!text.trim() || busy}
          onClick={save}
          className="h-8 rounded-[var(--radius-ui)] bg-fg px-3 font-medium text-bg disabled:opacity-50"
        >
          {busy ? "Сохраняем…" : "Добавить комментарий"}
        </button>
      </div>
    </div>
  );
}

function ExpenseRow(props: {
  row: KassaExpense;
  canNote: boolean;
  onNotes: (notes: KassaNote[]) => void;
  onFailure: (message: string) => void;
}) {
  const { row } = props;
  const [noteOpen, setNoteOpen] = useState(false);
  const back = Number(row.amount) < 0;
  return (
    <>
      <tr data-annot={`расход CRM ${row.crm_id}`} className={`hover:bg-bg-hover ${row.date_deleted ? "opacity-60" : ""}`}>
        <td className={`${TD} whitespace-nowrap tabular-nums text-fg-muted`}>
          {when(row.date).split(" ")[0]}
          {row.date_deleted ? <div className="text-[10.5px] text-err">пропала в CRM</div> : null}
        </td>
        <td className={`${TD} whitespace-nowrap text-right tabular-nums ${back ? "text-ok" : ""}`}>
          {back ? "+" : "−"}
          {money(Math.abs(Number(row.amount)))}
        </td>
        <td className={`${TD} whitespace-nowrap text-[11.5px]`}>
          <div>{row.kassa_name}</div>
          <div className="text-fg-faint">
            {KASSA_KIND_LABEL[row.kassa_kind]}
            {row.entrepreneur_name ? ` · ${row.entrepreneur_name}` : ""}
            {row.location_name ? ` · ${row.location_name}` : ""}
          </div>
        </td>
        <td className={`${TD} whitespace-nowrap text-[11.5px]`}>
          <div>{row.type || "—"}</div>
          {row.user_name ? (
            <div>
              {row.user_name}
              {row.user_role ? <span className="text-fg-faint"> · {row.user_role}</span> : null}
            </div>
          ) : row.recipient ? (
            <div>{row.recipient}</div>
          ) : null}
          {row.card_mask || row.recipient_bank ? (
            <div className="tabular-nums text-fg-faint">
              {row.card_mask ? `карта ${row.card_mask}` : ""}
              {row.card_mask && row.recipient_bank ? " · " : ""}
              {row.recipient_bank ?? ""}
            </div>
          ) : null}
        </td>
        <td className={`${TD} max-w-[360px]`}>
          <div className="flex items-start gap-1">
            <div className="line-clamp-2 min-w-0 flex-1 text-fg-muted" title={row.comment}>
              {row.comment}
            </div>
            {props.canNote ? (
              <button
                type="button"
                title="Комментарий: куда разнести, что выяснили"
                onClick={() => setNoteOpen(!noteOpen)}
                className={`rounded p-1 hover:bg-bg-hover hover:text-fg ${row.notes.length ? "text-accent-text" : "text-fg-faint"}`}
              >
                <MessageSquare size={13} />
              </button>
            ) : null}
          </div>
          <Comments items={row.comments} />
          <Notes items={row.notes} />
        </td>
        <td className={`${TD} max-w-[260px] text-[11.5px]`}>
          {row.invoice ? (
            <>
              <div className="whitespace-nowrap">
                №{row.invoice.crm_id} · {row.invoice.status}
                {row.invoice.category ? ` · ${row.invoice.category}` : ""}
              </div>
              <div className="whitespace-nowrap tabular-nums text-fg-faint">
                выплачено {money(row.invoice.paid)} из {money(row.invoice.amount)}
              </div>
              {row.invoice.comment ? (
                <div className="line-clamp-2 text-fg-muted" title={row.invoice.comment}>
                  {row.invoice.comment}
                </div>
              ) : null}
              <Comments items={row.invoice.comments} />
            </>
          ) : (
            <span className="text-fg-faint">без заявки</span>
          )}
        </td>
        <td className={`${TD} min-w-[200px] text-[11.5px]`}>
          <div className={row.article_name ? "" : "text-warn"}>{row.article_name ?? "не сопоставлена"}</div>
          {row.article_source === "type" || row.article_source === "comment" ? (
            <div className="text-fg-muted">{row.article_source === "type" ? "по типу расхода" : "по комментарию"}</div>
          ) : null}
          <div className="text-fg-faint">{row.crm_article_name ?? "в CRM без статьи"}</div>
        </td>
      </tr>
      {noteOpen ? (
        <tr>
          <td colSpan={7} className="border-b border-border bg-bg-subtle px-3 py-3">
            <NoteForm
              id={row.id}
              onSaved={(notes) => {
                setNoteOpen(false);
                props.onNotes(notes);
              }}
              onFailure={props.onFailure}
            />
          </td>
        </tr>
      ) : null}
    </>
  );
}
